import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  getPatientById,
  getPatientCharts,
  addPatientEpisode,
  updatePatientEpisode,
} from "../../services/patientService";
import {
  DocumentsIcon,
  CalendarIcon,
  ChevronRightIcon,
  EditIcon,
} from "../../Icons";
import StatusBadge, {
  CHART_STATUS_LABELS,
  STATUS_STYLES,
  isClosedChartStatus,
} from "./components/StatusBadge";
import { CollapseToggle } from "./components/CollapsibleSection";
import PatientSearchToolbar from "./components/PatientSearchToolbar";
import EpisodeDateModal from "./components/EpisodeDateModal";
import { displayDateUS, formatDateForInput } from "../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "./patientPageLayout";
import { canEditDocsNotes, canManageDemographics } from "./patientPermissions";
import { resolveLatestEpisodePath } from "./resolveLatestEpisodePath";
import usePatientTabs from "./usePatientTabs";
import { pickDefaultTab } from "./patientTabs";

const linkBtnClass =
  "text-[12px] font-medium text-indigo-700 hover:text-indigo-900 hover:underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed whitespace-nowrap";

const formatEpisodeRange = (ep) => {
  const start = displayDateUS(ep.startDate) || "—";
  const end = displayDateUS(ep.endDate) || "—";
  return `${start} – ${end}`;
};

const latestEpisode = (chart) => {
  const list = chart?.episodes || [];
  if (!list.length) return null;
  return [...list].sort((a, b) => b.episodeNumber - a.episodeNumber)[0];
};

const nextEpisodeDefaultStart = (chart) => {
  const latest = latestEpisode(chart);
  if (!latest?.endDate) {
    return formatDateForInput(chart?.startDate) || formatDateForInput(new Date());
  }
  const ymd = formatDateForInput(latest.endDate);
  if (!ymd) return formatDateForInput(new Date());
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return dt.toISOString().slice(0, 10);
};

/**
 * Patient Chart hub — nested charts / episodes with +/− collapse and date management.
 */
const PatientChartsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Charts is also the fallback landing page for callers outside the patient
  // list, so Back follows whoever sent us here.
  const backPath = location.state?.returnTo || "/patients";
  const backLabel = location.state?.backLabel || "Back to patients list";
  const [patient, setPatient] = useState(null);
  const [charts, setCharts] = useState([]);
  const [canAddChart, setCanAddChart] = useState(false);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [episodeModal, setEpisodeModal] = useState(null);
  const [savingEpisode, setSavingEpisode] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const role = currentUser?.role?.name;
  const canEdit = canEditDocsNotes(role);
  /** Episode add/edit — Admin & OA only. */
  const canManageEpisodes = canManageDemographics(role);
  const { isTabEnabled, defaultTab, enabledTabKeys } = usePatientTabs();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [patientRes, chartsRes] = await Promise.all([
        getPatientById(id),
        getPatientCharts(id),
      ]);
      const p =
        patientRes.result?.patient ||
        patientRes.patient ||
        patientRes.result ||
        null;
      const payload = chartsRes.result || chartsRes;
      const list = payload?.charts || [];
      const chartsList = Array.isArray(list) ? list : [];
      // Prefer open charts at the top so the hub highlights the active stay.
      chartsList.sort((a, b) => {
        const aOpen = !isClosedChartStatus(a.status);
        const bOpen = !isClosedChartStatus(b.status);
        if (aOpen !== bOpen) return aOpen ? -1 : 1;
        return (b.chartNumber || 0) - (a.chartNumber || 0);
      });
      setPatient(p);
      setCharts(chartsList);
      setCanAddChart(Boolean(payload?.canAddChart));
      const openMap = {};
      chartsList.forEach((c, index) => {
        openMap[c.id] = index === 0 || !isClosedChartStatus(c.status);
      });
      setExpanded(openMap);
    } catch (err) {
      toast.error(err.message || "Failed to load charts");
      navigate("/patients");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  /** Search select → open that patient's latest chart / latest episode. */
  const handleSelectPatient = useCallback(
    async (patientId) => {
      if (!patientId) return;
      if (String(patientId) === String(id)) {
        const chart = [...charts].sort(
          (a, b) => (b.chartNumber || 0) - (a.chartNumber || 0),
        )[0];
        const ep = chart ? latestEpisode(chart) : null;
        if (chart?.id && ep?.id) {
          navigate(
            `/patients/${patientId}/charts/${chart.id}/episodes/${ep.id}`,
          );
          return;
        }
      }
      setSwitching(true);
      try {
        const chartsRes = await getPatientCharts(patientId);
        const payload = chartsRes.result || chartsRes;
        navigate(resolveLatestEpisodePath(patientId, payload));
      } catch (err) {
        toast.error(err.message || "Failed to open patient");
        navigate(`/patients/${patientId}/charts`);
      } finally {
        setSwitching(false);
      }
    },
    [charts, id, navigate],
  );

  const toggleChart = (chartId) => {
    setExpanded((prev) => ({ ...prev, [chartId]: !prev[chartId] }));
  };

  const openEpisode = (chartId, episodeId, tab) => {
    const requested = tab || null;
    const targetTab =
      requested && isTabEnabled(requested)
        ? requested
        : pickDefaultTab(enabledTabKeys) || defaultTab || "demographics";
    if (requested && requested !== targetTab) {
      toast.error("That patient tab is not enabled for this agency");
    }
    navigate(
      {
        pathname: `/patients/${id}/charts/${chartId}/episodes/${episodeId}`,
        search: `?tab=${encodeURIComponent(targetTab)}`,
      },
      { state: { tab: targetTab } },
    );
  };

  const resolveEpisodeTarget = (chart) => {
    const ep = latestEpisode(chart);
    if (!ep) {
      toast.error("No episode found for this chart");
      return null;
    }
    return ep;
  };

  const handlePlanOfCare = (chart) => {
    if (!isTabEnabled("eval")) {
      toast.error("Eval & Orders is not enabled for this agency");
      return;
    }
    const ep = resolveEpisodeTarget(chart);
    if (!ep) return;
    openEpisode(chart.id, ep.id, "eval");
  };

  const openAddEpisode = (chart) => {
    if (!canManageEpisodes) {
      toast.error("Only Admin/OA can add episodes");
      return;
    }
    if (isClosedChartStatus(chart.status)) {
      toast.error("Cannot add an episode to a discharged chart");
      return;
    }
    setEpisodeModal({
      mode: "add",
      chart,
      episode: null,
      defaultStart: nextEpisodeDefaultStart(chart),
    });
  };

  const openEditEpisode = (chart, episode) => {
    if (!canManageEpisodes) {
      toast.error("Only Admin/OA can edit episodes");
      return;
    }
    setEpisodeModal({
      mode: "edit",
      chart,
      episode,
      defaultStart: "",
    });
  };

  const handleEpisodeSubmit = async (payload) => {
    if (!episodeModal) return;
    setSavingEpisode(true);
    try {
      if (episodeModal.mode === "edit" && episodeModal.episode?.id) {
        await updatePatientEpisode(
          id,
          episodeModal.chart.id,
          episodeModal.episode.id,
          payload,
        );
        toast.success(
          `Episode #${episodeModal.episode.episodeNumber || ""} updated`,
        );
      } else {
        const res = await addPatientEpisode(
          id,
          episodeModal.chart.id,
          payload,
        );
        const episode = res.result?.episode || res.episode;
        toast.success(`Episode #${episode?.episodeNumber || ""} created`);
        setExpanded((prev) => ({ ...prev, [episodeModal.chart.id]: true }));
        setEpisodeModal(null);
        await load();
        if (episode?.id) openEpisode(episodeModal.chart.id, episode.id);
        return;
      }
      setEpisodeModal(null);
      await load();
    } catch (err) {
      toast.error(err.message || "Failed to save episode");
    } finally {
      setSavingEpisode(false);
    }
  };

  if (loading || switching) {
    return (
      <div className={`${PATIENT_PAGE_SHELL} animate-pulse`}>
        <div className="h-10 bg-slate-100 border border-slate-200 rounded-lg" />
        <div className="h-40 bg-slate-100 border border-slate-200 rounded-lg" />
      </div>
    );
  }

  if (!patient) return null;

  const patientIdLabel = patient.mrn || patient.patientNumber || "—";

  return (
    <div className={PATIENT_PAGE_SHELL}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-slate-700">Charts</span>
        <div className="flex-1" />
        {canEdit && canAddChart ? (
          <button
            type="button"
            onClick={() => navigate(`/patients/${id}/charts/new`)}
            className="inline-flex items-center h-8 px-3 text-[11px] font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50"
          >
            Add New Chart
          </button>
        ) : null}
      </div>

      <PatientSearchToolbar
        patient={patient}
        onSelectPatient={handleSelectPatient}
        onClose={() => navigate(backPath)}
        closeTitle={backLabel}
      />

      <div className="space-y-3">
        {charts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center">
            <DocumentsIcon size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium text-slate-600">No charts yet</p>
            {canEdit ? (
              <button
                type="button"
                onClick={() => navigate(`/patients/${id}/charts/new`)}
                className="mt-3 inline-flex h-8 px-3 text-[11px] font-medium text-indigo-700 bg-white border border-indigo-300 rounded-md hover:bg-indigo-50"
              >
                Add New Chart
              </button>
            ) : null}
          </div>
        ) : (
          charts.map((chart) => {
            const isOpen = expanded[chart.id] !== false;
            const closed = isClosedChartStatus(chart.status);
            const statusStyle =
              STATUS_STYLES[chart.status] || STATUS_STYLES.Hold;

            return (
              <section
                key={chart.id}
                className={`rounded-xl border shadow-sm overflow-hidden ${
                  closed
                    ? "border-slate-200 bg-slate-50/40"
                    : "border-indigo-200 bg-indigo-50/40"
                }`}
              >
                <div
                  className={`flex flex-wrap items-center gap-2 px-4 py-3 border-b ${
                    closed
                      ? "bg-slate-100/80 border-slate-200"
                      : "bg-indigo-50 border-indigo-100"
                  }`}
                >
                  <CollapseToggle
                    open={isOpen}
                    onToggle={() => toggleChart(chart.id)}
                    labelOpen="Hide episodes"
                    labelClosed="Show episodes"
                  />

                  <span
                    className={`inline-flex h-7 w-7 items-center justify-center rounded-md shrink-0 ${
                      closed
                        ? "bg-slate-200 text-slate-600"
                        : "bg-white text-indigo-700 border border-indigo-200"
                    }`}
                    title="Chart"
                  >
                    <DocumentsIcon size={14} />
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2
                        className={`text-xs font-semibold uppercase tracking-wide ${
                          closed ? "text-slate-800" : "text-blue-950"
                        }`}
                      >
                        Chart #{chart.chartNumber}
                        <span className="font-medium normal-case tracking-normal text-slate-500">
                          {" "}
                          — {patientIdLabel}
                        </span>
                      </h2>
                      <StatusBadge status={chart.status} chartStyle />
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Start {displayDateUS(chart.startDate) || "—"}
                      {chart.dischargeDate
                        ? ` · Discharge ${displayDateUS(chart.dischargeDate)}`
                        : ""}
                      <span className={`ml-1 font-medium ${statusStyle.text}`}>
                        · {CHART_STATUS_LABELS[chart.status] || chart.status}
                      </span>
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 shrink-0">
                    {isTabEnabled("eval") ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handlePlanOfCare(chart)}
                          className={linkBtnClass}
                        >
                          Eval & Orders
                        </button>
                        <span className="text-slate-300" aria-hidden>
                          |
                        </span>
                      </>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => openAddEpisode(chart)}
                      disabled={!canManageEpisodes || closed}
                      title={
                        !canManageEpisodes
                          ? "Only Admin/OA can add episodes"
                          : closed
                            ? "Chart is discharged"
                            : "Add a new episode (default 60 days)"
                      }
                      className={linkBtnClass}
                    >
                      Add a New Episode
                    </button>
                    <span className="text-slate-300" aria-hidden>
                      |
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/patients/${id}/charts/${chart.id}/edit`)
                      }
                      disabled={!canEdit}
                      className={linkBtnClass}
                    >
                      Edit Chart
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="p-3 space-y-2 bg-white">
                    {(chart.episodes || []).length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-8">
                        No episodes in this chart
                      </p>
                    ) : (
                      [...(chart.episodes || [])]
                        .sort((a, b) => a.episodeNumber - b.episodeNumber)
                        .map((ep) => {
                          const done =
                            ep.isExpired || ep.status === "Completed";
                          return (
                            <div
                              key={ep.id}
                              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 shadow-sm hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors group"
                            >
                              <button
                                type="button"
                                onClick={() => openEpisode(chart.id, ep.id)}
                                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                              >
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-100 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-700 shrink-0">
                                  <CalendarIcon size={15} />
                                </span>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-slate-900">
                                    Episode #{ep.episodeNumber}
                                  </p>
                                  <p className="text-[11px] text-slate-500 mt-0.5">
                                    {formatEpisodeRange(ep)}
                                    {" · "}
                                    {done ? "Completed" : "Active"}
                                  </p>
                                </div>
                                <ChevronRightIcon
                                  size={16}
                                  className="text-slate-300 group-hover:text-blue-500 shrink-0"
                                />
                              </button>
                              {canManageEpisodes ? (
                                <button
                                  type="button"
                                  onClick={() => openEditEpisode(chart, ep)}
                                  className="inline-flex items-center gap-1 h-7 px-2 text-[11px] font-medium text-indigo-700 bg-white border border-indigo-200 rounded-md hover:bg-indigo-50 shrink-0"
                                  title="Edit episode dates"
                                >
                                  <EditIcon size={12} />
                                  Edit
                                </button>
                              ) : null}
                            </div>
                          );
                        })
                    )}
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>

      <EpisodeDateModal
        open={Boolean(episodeModal)}
        mode={episodeModal?.mode || "add"}
        chart={episodeModal?.chart}
        episode={episodeModal?.episode}
        defaultStart={episodeModal?.defaultStart}
        saving={savingEpisode}
        onClose={() => setEpisodeModal(null)}
        onSubmit={handleEpisodeSubmit}
      />
    </div>
  );
};

export default PatientChartsPage;
